// Google Cloud Vision API Service for OCR
// You'll need to get an API key from https://console.cloud.google.com/

const GOOGLE_VISION_API_KEY = import.meta.env.VITE_GOOGLE_VISION_API_KEY || 'your-google-vision-api-key-here';
const GOOGLE_VISION_API_URL = 'https://vision.googleapis.com/v1/images:annotate';

class OCRService {
  constructor() {
    this.apiKey = GOOGLE_VISION_API_KEY;
    this.apiUrl = GOOGLE_VISION_API_URL;
  }

  // Extract VIN from image using Google Vision API
  async extractVinFromImage(imageData) {
    try {
      if (!this.apiKey || this.apiKey === 'your-google-vision-api-key-here') {
        throw new Error('Google Vision API key not configured. Please add VITE_GOOGLE_VISION_API_KEY to your environment variables.');
      }

      // Remove data URL prefix to get base64 data
      const base64Data = imageData.replace(/^data:image\/[a-z]+;base64,/, '');

      const requestBody = {
        requests: [
          {
            image: {
              content: base64Data
            },
            features: [
              {
                type: 'TEXT_DETECTION',
                maxResults: 1
              }
            ]
          }
        ]
      };

      const response = await fetch(`${this.apiUrl}?key=${this.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        if (response.status === 400) {
          throw new Error('Ungültiges Bildformat. Bitte versuchen Sie es mit einem anderen Bild.');
        } else if (response.status === 401) {
          throw new Error('Ungültiger Google Vision API-Schlüssel');
        } else if (response.status === 429) {
          throw new Error('API-Limit erreicht. Bitte versuchen Sie es später erneut.');
        } else {
          throw new Error(`Google Vision API-Fehler: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();
      
      if (!data.responses || !data.responses[0]) {
        throw new Error('Keine Textantwort von der Google Vision API erhalten');
      }

      const textAnnotations = data.responses[0].textAnnotations;
      
      if (!textAnnotations || textAnnotations.length === 0) {
        throw new Error('Kein Text im Bild gefunden. Bitte stellen Sie sicher, dass der VIN-Code klar sichtbar ist.');
      }

      // Extract all detected text
      const fullText = textAnnotations[0].description;
      
      // Find VIN in the extracted text
      const vin = this.findVinInText(fullText);
      
      if (!vin) {
        throw new Error('Kein gültiger VIN-Code im Bild gefunden. Bitte versuchen Sie es mit einem anderen Bild.');
      }

      return vin;
    } catch (error) {
      console.error('OCR error:', error);
      throw error;
    }
  }

  // Find VIN in extracted text
  findVinInText(text) {
    if (!text) return null;

    // Convert to uppercase and remove spaces
    const cleanText = text.toUpperCase().replace(/\s/g, '');
    
    // Look for 17-character VIN patterns
    const vinPattern = /[A-HJ-NPR-Z0-9]{17}/g;
    const matches = cleanText.match(vinPattern);
    
    if (matches && matches.length > 0) {
      // Return the first valid VIN found
      return matches[0];
    }

    // If no exact 17-character match, look for VIN-like patterns
    const words = text.toUpperCase().split(/\s+/);
    
    for (const word of words) {
      const cleanWord = word.replace(/[^A-HJ-NPR-Z0-9]/g, '');
      if (cleanWord.length === 17) {
        return cleanWord;
      }
    }

    return null;
  }

  // Validate VIN format
  validateVin(vin) {
    if (!vin) return false;
    
    // Remove spaces and convert to uppercase
    const cleanVin = vin.replace(/\s/g, '').toUpperCase();
    
    // Check length (VINs are typically 17 characters)
    if (cleanVin.length !== 17) {
      return false;
    }
    
    // Check for valid characters (no I, O, Q)
    const invalidChars = /[IOQ]/;
    if (invalidChars.test(cleanVin)) {
      return false;
    }
    
    return true;
  }

  // Test OCR with a sample image (for development)
  async testOCR() {
    try {
      // Create a simple test image with text
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 400;
      canvas.height = 100;
      
      // Draw white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 400, 100);
      
      // Draw test VIN
      ctx.fillStyle = 'black';
      ctx.font = '24px Arial';
      ctx.fillText('WBAVA31070NL12345', 20, 60);
      
      const testImageData = canvas.toDataURL('image/jpeg', 0.8);
      
      const result = await this.extractVinFromImage(testImageData);
      console.log('OCR Test Result:', result);
      return result;
    } catch (error) {
      console.error('OCR Test Error:', error);
      throw error;
    }
  }
}

export default new OCRService();
